import { useTranslation } from "react-i18next";

import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { useMutation } from "@/modules/api/hooks/useMutation";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import { signInMutation } from "@/modules/auth/api/mutations/signIn/signInMutation";
import { completeStepMutation } from "@/modules/tutorials/api/mutations/completeStepMutation";

import { useHandleTutorialsNavigation } from "./useHandleTutorialsNavigation";

const translationNamespace = "apiMessages/useCompleteStepMutation";

type UseCompleteStepMutationParams = {
  tutorialId: string | undefined;
  nextStep: number | null;
  testAvailable: boolean;
};

export const useCompleteStepMutation = ({
  tutorialId,
  nextStep,
  testAvailable,
}: UseCompleteStepMutationParams) => {
  const jsonApi = useJsonApi();
  const { t } = useTranslation(translationNamespace);
  const handleTutorialsNavigation = useHandleTutorialsNavigation();

  return useMutation([signInMutation.name], {
    mutationFn: completeStepMutation(jsonApi),
    onSuccess: () => {
      if (tutorialId) {
        handleTutorialsNavigation({
          nextStep,
          tutorialId,
          testAvailable,
        });
      }
    },
    onError: (error) => {
      apiErrorValidate(error, {
        401: {
          UNAUTHORIZED: t(
            "You are not allowed to complete the step. Please sign in again"
          ),
        },
        403: {
          FORBIDDEN: t("You don't have an access to complete the step"),
        },
        404: {
          TUTORIAL_NOT_FOUND: t("The tutorial or step cannot cannot be found"),
        },
        422: {
          TUTORIAL_UPDATED: t(
            "The tutorial has been updated. Please refresh the app"
          ),
        },
      });
    },
    retry: false,
  });
};
